/**
 * Documentation navigation structure.
 * Defines the sidebar tree.
 */

export interface NavSection {
  /** Unique identifier for state management (expansion tracking) */
  id: string;
  /** Display title in sidebar */
  title: string;
  /** URL slug for routing - can include hash for anchor links */
  slug: string;
  /** Optional child sections (creates collapsible group if present) */
  children?: NavSection[];
}

/**
 * Main documentation navigation tree.
 */
export const docsNavigation: NavSection[] = [
  {
    id: 'getting-started',
    slug: 'getting-started',
    title: 'Getting Started',
  },
  {
    id: 'product-creator-setup',
    slug: 'product-creator-setup',
    title: 'Product Creator Setup',
  },
  {
    children: [
      {
        id: 'what-is-compounds',
        slug: 'what-is-compounds',
        title: 'What is Compounds?',
      },
      {
        id: 'how-to-use',
        slug: 'how-to-use',
        title: 'How to Use Compounds',
      },
      {
        id: 'reference',
        slug: 'reference',
        title: 'Workflow Reference',
      },
      {
        id: 'troubleshooting',
        slug: 'troubleshooting',
        title: 'Troubleshooting',
      },
    ],
    id: 'visual-studio-setup',
    slug: 'what-is-compounds',
    title: 'Developer Setup',
  },
];

/**
 * Find parent section for a given child slug.
 */
export function findParentSection(childSlug: string): NavSection | undefined {
  for (const section of docsNavigation) {
    if (section.children) {
      for (const child of section.children) {
        if (child.slug === childSlug || child.slug.split('#')[0] === childSlug) {
          return section;
        }
        // Check grandchildren
        if (child.children) {
          for (const grandchild of child.children) {
            if (grandchild.slug === childSlug || grandchild.slug.split('#')[0] === childSlug) {
              return child;
            }
          }
        }
      }
    }
  }
  return undefined;
}

/**
 * Helper to find a section by its slug (for breadcrumb building).
 */
export function findSectionBySlug(slug: string): NavSection | undefined {
  for (const section of docsNavigation) {
    if (section.children) {
      for (const child of section.children) {
        if (child.slug === slug || child.slug.split('#')[0] === slug) {
          return child;
        }
        // Check grandchildren
        if (child.children) {
          for (const grandchild of child.children) {
            if (grandchild.slug === slug || grandchild.slug.split('#')[0] === slug) {
              return grandchild;
            }
          }
        }
      }
    }
    if (section.slug === slug || section.slug.split('#')[0] === slug) {
      return section;
    }
  }
  return undefined;
}

/**
 * Get the base slug (without hash anchor) from a slug.
 */
export function getBaseSlug(slug: string): string {
  return slug.split('#')[0];
}
